// SPDX-License-Identifier: AGPL-3.0-only

import {
  createRouter,
  createWebHistory,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
  type Router,
} from "vue-router";
import type { TenantSummary } from "../api/tenants.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { pinia } from "../stores/pinia.js";
import { useTenantStore } from "../stores/tenants.js";
import DashboardView from "../views/DashboardView.vue";
import LoginView from "../views/LoginView.vue";
import NoTenantsView from "../views/NoTenantsView.vue";
import NotificationContactOptInView from "../views/NotificationContactOptInView.vue";
import PrivacyView from "../views/PrivacyView.vue";
import SystemDashboardView from "../views/SystemDashboardView.vue";
import SystemTenantsView from "../views/SystemTenantsView.vue";
import SystemUsersView from "../views/SystemUsersView.vue";
import SystemSettingsView from "../views/SystemSettingsView.vue";
import SystemNotificationsView from "../views/SystemNotificationsView.vue";
import SystemFirmwareView from "../views/SystemFirmwareView.vue";
import SystemTiersView from "../views/SystemTiersView.vue";
import TenantMembersView from "../views/TenantMembersView.vue";
import TenantNotificationsView from "../views/TenantNotificationsView.vue";
import TenantProbesView from "../views/TenantProbesView.vue";
import TenantProbeConfigurationView from "../views/TenantProbeConfigurationView.vue";
import TenantProbeFirmwarePolicyView from "../views/TenantProbeFirmwarePolicyView.vue";
import TenantLatencyDashboardView from "../views/TenantLatencyDashboardView.vue";
import InvitationView from "../views/InvitationView.vue";
import ResetPasswordView from "../views/ResetPasswordView.vue";
import SystemInvitationView from "../views/SystemInvitationView.vue";
import UserSettingsView from "../views/UserSettingsView.vue";
import { canManageSystemUsers, canManageTenants, canViewSystem } from "../api/system-authorization.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

export const router: Router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/invitation", name: "invitation", component: InvitationView },
    { path: "/system-invitation", name: "system-invitation", component: SystemInvitationView },
    { path: "/reset-password", name: "reset-password", component: ResetPasswordView },
    {
      path: "/login",
      name: "login",
      component: LoginView,
      meta: { requiresAnonymous: true },
    },
    {
      path: "/privacy",
      name: "privacy",
      component: PrivacyView,
    },
    {
      path: "/notification-contact-opt-in",
      name: "notification-contact-opt-in",
      component: NotificationContactOptInView,
    },
    {
      path: "/",
      name: "home",
      component: NoTenantsView,
      meta: { requiresAuthentication: true },
    },
    {
      path: "/admin",
      name: "system-dashboard",
      component: SystemDashboardView,
      meta: { requiresAuthentication: true, requiresSystemAccess: true },
    },
    {
      path: "/admin/tenants",
      name: "system-tenants",
      component: SystemTenantsView,
      meta: { requiresAuthentication: true, requiresSystemAccess: true },
    },
    {
      path: "/admin/users",
      name: "system-users",
      component: SystemUsersView,
      meta: { requiresAuthentication: true, requiresSystemAdministrator: true },
    },
    {
      path: "/admin/tiers",
      name: "system-tiers",
      component: SystemTiersView,
      meta: { requiresAuthentication: true, requiresSystemAdministrator: true },
    },
    {
      path: "/admin/firmware",
      name: "system-firmware",
      component: SystemFirmwareView,
      meta: { requiresAuthentication: true, requiresSystemAdministrator: true },
    },
    {
      path: "/admin/settings",
      name: "system-settings",
      component: SystemSettingsView,
      meta: { requiresAuthentication: true, requiresSystemAdministrator: true },
    },
    {
      path: "/admin/notifications",
      name: "system-notifications",
      component: SystemNotificationsView,
      meta: { requiresAuthentication: true, requiresSystemAdministrator: true },
    },
    {
      path: "/tenants/:tenantId/overview",
      name: "tenant-overview",
      component: DashboardView,
      meta: { requiresAuthentication: true, requiresTenant: true },
    },
    {
      path: "/tenants/:tenantId/dashboard",
      name: "tenant-dashboard",
      component: TenantLatencyDashboardView,
      meta: { requiresAuthentication: true, requiresTenant: true, requiresMeasurementRead: true },
    },
    {
      path: "/tenants/:tenantId/probes",
      name: "tenant-probes",
      component: TenantProbesView,
      meta: {
        requiresAuthentication: true,
        requiresTenant: true,
        requiresProbeRead: true,
      },
    },
    {
      path: "/tenants/:tenantId/probes/configuration",
      name: "tenant-probe-configuration",
      component: TenantProbeConfigurationView,
      meta: {
        requiresAuthentication: true,
        requiresTenant: true,
        requiresProbeConfigurationManagement: true,
      },
    },
    {
      path: "/tenants/:tenantId/probes/rollout",
      name: "tenant-probe-rollout",
      component: TenantProbeFirmwarePolicyView,
      meta: {
        requiresAuthentication: true,
        requiresTenant: true,
        requiresProbeRead: true,
      },
    },
    {
      path: "/tenants/:tenantId/probe-configuration",
      redirect: (to) => ({
        name: "tenant-probe-configuration",
        params: { tenantId: to.params.tenantId },
      }),
    },
    {
      path: "/tenants/:tenantId/members",
      name: "tenant-members",
      component: TenantMembersView,
      meta: {
        requiresAuthentication: true,
        requiresTenant: true,
        requiresTenantMemberManagement: true,
      },
    },
    {
      path: "/tenants/:tenantId/notifications",
      name: "tenant-notifications",
      component: TenantNotificationsView,
      meta: {
        requiresAuthentication: true,
        requiresTenant: true,
        requiresTenantNotificationRead: true,
      },
    },
    {
      path: "/no-tenants",
      name: "no-tenants",
      component: NoTenantsView,
      meta: { requiresAuthentication: true },
    },
    {
      path: "/user/settings",
      name: "user-settings",
      component: UserSettingsView,
      meta: { requiresAuthentication: true },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/",
    },
  ],
});

router.beforeEach(async (to: RouteLocationNormalized): Promise<NavigationGuardReturn> => {
  const authenticationStore: ReturnType<typeof useAuthenticationStore> =
    useAuthenticationStore(pinia);
  const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore(pinia);
  const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore(pinia);

  try {
    await authenticationStore.initialize();
  } catch (_error: unknown) {
    if (to.name !== "login") {
      return { name: "login", query: { unavailable: "1" } };
    }
  }

  if (to.meta.requiresAuthentication === true && !authenticationStore.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAnonymous === true && authenticationStore.isAuthenticated) {
    return { name: "home" };
  }
  if (!authenticationStore.isAuthenticated || authenticationStore.session === null) {
    return true;
  }

  try {
    await userSettingsStore.initialize();
  } catch (_error: unknown) {
    // User settings are display preferences only. Navigation must continue with
    // safe default formatting if the settings endpoint is temporarily unavailable.
  }

  const isSystemAdministrator: boolean =
    authenticationStore.session.user.systemRoles.includes("system_admin");
  const hasSystemAccess: boolean = canViewSystem(authenticationStore.session.user.systemRoles);
  try {
    await tenantStore.initialize(authenticationStore.session.user.id);
  } catch (_error: unknown) {
    if (to.name === "no-tenants" && to.query.unavailable === "1") {
      return true;
    }
    return { name: "no-tenants", query: { unavailable: "1" } };
  }

  if (to.name === "home") {
    if (hasSystemAccess) {
      return { name: "system-dashboard" };
    }
    return tenantStore.selectedTenantId === null
      ? { name: "no-tenants" }
      : { name: "tenant-overview", params: { tenantId: tenantStore.selectedTenantId } };
  }
  if (to.meta.requiresSystemAdministrator === true && !isSystemAdministrator) {
    return { name: "home" };
  }
  if (to.meta.requiresSystemAccess === true && !hasSystemAccess) {
    return { name: "home" };
  }
  if (to.meta.requiresTenantManagement === true && !canManageTenants(authenticationStore.session.user.systemRoles)) {
    return { name: "system-dashboard" };
  }
  if (to.meta.requiresSystemUserManagement === true && !canManageSystemUsers(authenticationStore.session.user.systemRoles)) {
    return { name: "system-dashboard" };
  }
  if (to.name === "no-tenants" && tenantStore.selectedTenantId !== null) {
    return { name: "tenant-overview", params: { tenantId: tenantStore.selectedTenantId } };
  }
  if (to.meta.requiresTenant === true) {
    const routeTenantId: string | null =
      typeof to.params.tenantId === "string" ? to.params.tenantId : null;
    const tenant: TenantSummary | null =
      tenantStore.tenants.find(
        (item: TenantSummary): boolean => item.id === routeTenantId,
      ) ?? null;
    if (routeTenantId === null || tenant === null) {
      return { name: "home" };
    }
    if (
      to.meta.requiresProbeRead === true &&
      !tenant.access.capabilities.includes("probe.read")
    ) {
      return { name: "tenant-overview", params: { tenantId: routeTenantId } };
    }
    if (
      to.meta.requiresMeasurementRead === true &&
      !tenant.access.capabilities.includes("measurement.read")
    ) {
      return { name: "tenant-overview", params: { tenantId: routeTenantId } };
    }
    if (
      to.meta.requiresTenantMemberManagement === true &&
      !tenant.access.capabilities.includes("tenant.members.manage")
    ) {
      return { name: "tenant-overview", params: { tenantId: routeTenantId } };
    }
    if (
      to.meta.requiresTenantNotificationRead === true &&
      !tenant.access.capabilities.includes("tenant.notifications.read")
    ) {
      return { name: "tenant-overview", params: { tenantId: routeTenantId } };
    }
    if (
      to.meta.requiresProbeConfigurationManagement === true &&
      !tenant.access.capabilities.includes("probe.configuration.manage")
    ) {
      return { name: "tenant-overview", params: { tenantId: routeTenantId } };
    }
    tenantStore.selectTenant(routeTenantId, authenticationStore.session.user.id);
  }

  return true;
});
